import { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faComputer, faEye, faGamepad, faCalendar, faUser } from '@fortawesome/free-solid-svg-icons'
import { useNavigate } from 'react-router-dom'
import { LoadingSpinner, EmptyState, Badge, Button } from '../shared/components/ui'
import { guestApi } from '../services/guestApi'
import type { SharedDevice } from '../types/guest'

export default function GuestDeviceListPage() {
  const navigate = useNavigate()
  const [devices, setDevices] = useState<SharedDevice[]>([])
  const [groupedDevices, setGroupedDevices] = useState<Record<string, SharedDevice[]>>({})
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadSharedDevices()
  }, [])

  const loadSharedDevices = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await guestApi.getSharedDevices()
      setDevices(response.devices)
      setGroupedDevices(response.groupedByOwner)
    } catch (err: any) {
      console.error('Failed to load shared devices:', err)
      setError(err.message || 'Failed to load shared devices')
    } finally {
      setIsLoading(false)
    }
  }

  const handleViewDevice = (deviceId: number) => {
    navigate(`/devices/${deviceId}`)
  }

  const getAccessLevelBadge = (accessLevel: string) => {
    return accessLevel === 'VIEW_ONLY' ? (
      <Badge variant="info" size="sm">
        <FontAwesomeIcon icon={faEye} className="mr-1 h-3 w-3" />
        View Only
      </Badge>
    ) : (
      <Badge variant="success" size="sm">
        <FontAwesomeIcon icon={faGamepad} className="mr-1 h-3 w-3" />
        Control
      </Badge>
    )
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-red-800 mb-2">Error Loading Devices</h3>
          <p className="text-red-700 mb-4">{error}</p>
          <Button variant="primary" onClick={loadSharedDevices}>
            Retry
          </Button>
        </div>
      </div>
    )
  }

  if (devices.length === 0) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Devices Shared With Me</h1>
          <p className="text-gray-600 mt-1">View and manage devices that have been shared with you</p>
        </div>
        <EmptyState
          title="No devices shared yet"
          description="No devices have been shared with you. Contact a device owner to request access to their devices."
        />
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Devices Shared With Me</h1>
        <p className="text-gray-600 mt-1">
          You have access to {devices.length} device{devices.length !== 1 ? 's' : ''} from{' '}
          {Object.keys(groupedDevices).length} owner{Object.keys(groupedDevices).length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Grouped Devices by Owner */}
      <div className="space-y-6">
        {Object.entries(groupedDevices).map(([ownerEmail, ownerDevices]) => (
          <div key={ownerEmail} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            {/* Owner Header */}
            <div className="bg-gray-50 border-b border-gray-200 px-6 py-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                  {ownerEmail.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                    <FontAwesomeIcon icon={faUser} className="mr-2 h-4 w-4 text-gray-400" />
                    {ownerEmail}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {ownerDevices.length} device{ownerDevices.length !== 1 ? 's' : ''} shared
                  </p>
                </div>
              </div>
            </div>

            {/* Device Cards */}
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {ownerDevices.map((device) => (
                <div
                  key={device.deviceId}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => handleViewDevice(device.deviceId)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start space-x-3">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <FontAwesomeIcon icon={faComputer} className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{device.deviceName}</h3>
                        <p className="text-sm text-gray-500">{device.wisolId}</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Access Level:</span>
                      {getAccessLevelBadge(device.accessLevel)}
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 flex items-center">
                        <FontAwesomeIcon icon={faCalendar} className="mr-2 h-3 w-3 text-gray-400" />
                        Shared:
                      </span>
                      <span className="text-sm text-gray-900">{formatDate(device.grantedAt)}</span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleViewDevice(device.deviceId)
                      }}
                      className="w-full"
                    >
                      View Device Details
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Help Text */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-blue-900 mb-2">About Device Access</h3>
        <ul className="space-y-2 text-sm text-blue-800">
          <li className="flex items-start">
            <FontAwesomeIcon icon={faEye} className="mr-2 mt-0.5 h-4 w-4 text-blue-600" />
            <span>
              <strong>View Only:</strong> You can view device data and readings
            </span>
          </li>
          <li className="flex items-start">
            <FontAwesomeIcon icon={faGamepad} className="mr-2 mt-0.5 h-4 w-4 text-blue-600" />
            <span>
              <strong>Control:</strong> You can view data and send commands to the device
            </span>
          </li>
        </ul>
        <p className="mt-4 text-sm text-blue-700">
          If you need access to additional devices, please contact the device owner to request an invitation.
        </p>
      </div>
    </div>
  )
}
